import { useState, useEffect, useMemo, useCallback, useRef, createContext, useContext, ReactNode } from 'react'
import { User, Session, AuthError } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { Profile, SubscriptionTier, ThemePreference } from '../types'
import { canAccess, Feature } from '../lib/permissions'

// ---------------------------------------------------------------------------
// Context type
// ---------------------------------------------------------------------------

interface AuthContextType {
  // Core auth
  user:           User | null
  session:        Session | null
  loading:        boolean
  profileLoading: boolean   // true until the profiles row has been fetched

  // Profile & subscription
  profile:  Profile | null
  tier:     SubscriptionTier
  isAdmin:  boolean
  isPaused: boolean

  // Permission helper — wraps canAccess() with the current user's tier
  canAccessFeature: (feature: Feature) => boolean

  // True while the user has a PASSWORD_RECOVERY session (clicked a reset link)
  isRecoveryMode: boolean

  // Silently re-fetch the profile row (e.g. after a Stripe checkout, to pick up
  // a webhook-applied tier change without a full page reload).
  refreshProfile: () => Promise<void>

  // Persist the user's theme choice to their profile so it follows them across
  // browsers/devices. No-op when signed out (localStorage-only in that case).
  updateThemePreference: (pref: ThemePreference) => Promise<void>

  // Auth actions
  signUp:         (email: string, password: string, username: string) => Promise<{ error: AuthError | null; needsVerification: boolean }>
  signIn:         (email: string, password: string)                   => Promise<{ error: AuthError | null }>
  signOut:        ()                                                  => Promise<{ error: AuthError | null }>
  updatePassword: (newPassword: string)                               => Promise<{ error: AuthError | null }>
}

// Invite token stashed at signup, redeemed once the user is authenticated
// (after email verification). See PENDING_INVITE_KEY usage below.
export const PENDING_INVITE_KEY = 'pendingInviteToken'

// ---------------------------------------------------------------------------
// DB row → Profile mapper
// ---------------------------------------------------------------------------

function mapProfile(row: Record<string, any>): Profile {
  return {
    id:                   row.id,
    email:                row.email ?? null,
    username:             row.username ?? null,
    subscriptionTier:     row.subscription_tier,
    accountStatus:        row.account_status,
    role:                 row.role,
    storageUsedBytes:     row.storage_used_bytes,
    stripeCustomerId:     row.stripe_customer_id ?? null,
    stripeSubscriptionId: row.stripe_subscription_id ?? null,
    invitedBy:            row.invited_by ?? null,
    themePreference:      row.theme_preference ?? 'dark',
    createdAt:            new Date(row.created_at),
    updatedAt:            new Date(row.updated_at),
    lastSignInAt:         row.last_sign_in_at ? new Date(row.last_sign_in_at) : null,
  }
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user,    setUser]    = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading,        setLoading]        = useState(true)
  const [profileLoading, setProfileLoading] = useState(true)
  const [profile,        setProfile]        = useState<Profile | null>(null)
  const [isRecoveryMode, setIsRecoveryMode] = useState(false)

  // Tracks which user's profile we've already fetched so auth events that
  // don't change the user (e.g. hourly token refreshes) don't refetch it.
  const fetchedProfileFor = useRef<string | null>(null)

  // `silent` re-fetches without toggling profileLoading — used by background
  // refreshes (e.g. polling for a post-checkout tier change) so the whole page
  // doesn't flash its loading state on every poll.
  async function fetchProfile(userId: string, opts?: { silent?: boolean }): Promise<void> {
    fetchedProfileFor.current = userId
    if (!opts?.silent) setProfileLoading(true)
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single()

      if (error) {
        // No profile row yet (e.g. existing user before migration, or trigger lag)
        console.warn('Could not load profile:', error.message)
        if (!opts?.silent) setProfile(null)
        return
      }

      setProfile(mapProfile(data))
    } catch (err) {
      console.warn('Unexpected error loading profile:', err)
      if (!opts?.silent) setProfile(null)
    } finally {
      if (!opts?.silent) setProfileLoading(false)
    }
  }

  useEffect(() => {
    // ------------------------------------------------------------------
    // Initial session load — runs once on mount.
    // setLoading(false) as soon as we know the auth state; profile fetch
    // is supplementary and must not block the app from rendering.
    // ------------------------------------------------------------------
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setUser(session?.user ?? null)
      setLoading(false)

      if (session?.user) {
        fetchProfile(session.user.id)
      } else {
        // No user — nothing to fetch
        setProfileLoading(false)
      }
    })

    // ------------------------------------------------------------------
    // Auth state changes (sign in, sign out, token refresh, etc.)
    //
    // IMPORTANT: Do NOT await Supabase calls inside this callback.
    // The Supabase JS v2 client holds an internal lock while firing auth
    // events. Making another Supabase request synchronously inside the
    // callback causes a deadlock and the app hangs on "Loading...".
    //
    // Solution: update React state immediately (no await), then defer any
    // follow-up Supabase calls via setTimeout to break out of the lock.
    // ------------------------------------------------------------------
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session)
        setUser(session?.user ?? null)
        setLoading(false)

        // Must be set before any early returns below.
        if (event === 'PASSWORD_RECOVERY') setIsRecoveryMode(true)

        if (session?.user) {
          const userId = session.user.id
          // TOKEN_REFRESHED fires hourly and on tab focus without changing
          // the user — refetching the profile there is pure waste. Likewise
          // skip any event for a user whose profile we already have, except
          // USER_UPDATED (email/metadata changes should refresh it).
          const alreadyFetched = fetchedProfileFor.current === userId
          if (event === 'TOKEN_REFRESHED' && alreadyFetched) return
          if (alreadyFetched && event !== 'USER_UPDATED') return

          setTimeout(() => { fetchProfile(userId) }, 0)
        } else {
          fetchedProfileFor.current = null
          setProfile(null)
          setProfileLoading(false)
        }
      }
    )

    return () => subscription.unsubscribe()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------------------
  // Redeem a pending invitation once the user is authenticated.
  //
  // Invited users sign up with a token, but with email verification on there's
  // no session at signup time — so redemption is deferred to here, after the
  // verification link establishes the session. The accept-invite endpoint
  // validates the token's email against the authenticated user, so this is
  // safe. Runs once per signed-in user; clears the token whether it succeeds
  // or is permanently rejected so it can't loop.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!user) return
    const token = localStorage.getItem(PENDING_INVITE_KEY)
    if (!token) return

    let cancelled = false
    ;(async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (!session?.access_token || cancelled) return

        const appUrl = process.env.REACT_APP_URL ?? ''
        const res = await fetch(`${appUrl}/api/webhooks/accept-invite`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ token }),
        })

        // Clear on success, or on a terminal rejection (used/expired/mismatch/
        // not found) that retrying won't fix. Keep it only on transient
        // failures (network, 500) so a later load can retry.
        const terminal = res.ok || [403, 404, 409, 410].includes(res.status)
        if (terminal) {
          localStorage.removeItem(PENDING_INVITE_KEY)
          if (res.ok && !cancelled) fetchProfile(user.id) // reflect new tier
        }
      } catch {
        // Transient (likely network) — leave the token for a future attempt.
      }
    })()

    return () => { cancelled = true }
  }, [user])

  // ---------------------------------------------------------------------------
  // Derived state
  // ---------------------------------------------------------------------------

  const tier:     SubscriptionTier = profile?.subscriptionTier ?? 'free'
  const isAdmin:  boolean          = profile?.role === 'admin'
  const isPaused: boolean          = profile?.accountStatus === 'paused'

  const canAccessFeature = useCallback(
    (feature: Feature): boolean => canAccess(tier, feature),
    [tier]
  )

  // ---------------------------------------------------------------------------
  // Auth actions
  // ---------------------------------------------------------------------------

  const signUp = useCallback(async (email: string, password: string, username: string) => {
    try {
      const base = process.env.REACT_APP_URL || window.location.origin
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { username },
          // Where the verification link returns the user. detectSessionInUrl
          // (default on) establishes the session from the URL hash on landing.
          emailRedirectTo: `${base}/`,
        },
      })
      // With "Confirm email" on, signUp returns no session until verified.
      return { error, needsVerification: !data.session }
    } catch (err) {
      return {
        error: { message: 'Network error - please check your connection and try again' } as any,
        needsVerification: false,
      }
    }
  }, [])

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password })
      return { error }
    } catch (err) {
      return { error: { message: 'Network error - please check your connection and try again' } as any }
    }
  }, [])

  const refreshProfile = useCallback(async () => {
    if (user) await fetchProfile(user.id, { silent: true })
  }, [user])

  const updateThemePreference = useCallback(async (pref: ThemePreference) => {
    if (!user) return
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ theme_preference: pref })
        .eq('id', user.id)
      if (error) {
        console.warn('Could not save theme preference:', error.message)
        return
      }
      // Keep the in-memory profile in sync so it stays consistent this session.
      setProfile(prev => (prev ? { ...prev, themePreference: pref } : prev))
    } catch (err) {
      console.warn('Unexpected error saving theme preference:', err)
    }
  }, [user])

  const signOut = useCallback(async () => {
    let error: AuthError | null = null
    try {
      const res = await supabase.auth.signOut()
      error = res.error
      // A global sign-out 403s when the access token has already expired. When
      // that happens the SDK can leave the session in storage and never emit
      // SIGNED_OUT, so the user appears stuck signed in. Fall back to a local
      // sign-out to guarantee the stored session is removed.
      if (error) {
        await supabase.auth.signOut({ scope: 'local' }).catch(() => {})
      }
    } catch (err) {
      error = err as AuthError
      await supabase.auth.signOut({ scope: 'local' }).catch(() => {})
    } finally {
      // Defensively reset local auth state so the UI always reflects logout,
      // even if the SDK's SIGNED_OUT event doesn't fire.
      fetchedProfileFor.current = null
      setUser(null)
      setSession(null)
      setProfile(null)
      setProfileLoading(false)
    }
    return { error }
  }, [])

  const updatePassword = useCallback(async (newPassword: string) => {
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword })
      if (!error) setIsRecoveryMode(false)
      return { error }
    } catch (err) {
      return { error: { message: 'Network error — please check your connection and try again' } as any }
    }
  }, [])

  // ---------------------------------------------------------------------------
  // Context value — memoized so AuthProvider re-renders don't cascade a new
  // object identity to every useAuth() consumer in the app.
  // ---------------------------------------------------------------------------

  const value: AuthContextType = useMemo(() => ({
    user,
    session,
    loading,
    profileLoading,
    profile,
    tier,
    isAdmin,
    isPaused,
    canAccessFeature,
    isRecoveryMode,
    refreshProfile,
    updateThemePreference,
    signUp,
    signIn,
    signOut,
    updatePassword,
  }), [
    user, session, loading, profileLoading, profile,
    tier, isAdmin, isPaused, canAccessFeature, isRecoveryMode,
    refreshProfile, updateThemePreference, signUp, signIn, signOut, updatePassword,
  ])

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
